<template>
  <mailbox-settings-layout :name="name">
    <mailbox-settings-collapsible-card title="Direct Connection" class="mailbox-settings-direct-connection">
      <entity-settings-loading v-if="isLoading" />

      <!-- CONNECTED GRANT -->
      <template v-else-if="hasGrant">
        <div class="direct-connection-details">
          <div class="direct-connection-details__row">
            <span class="direct-connection-details__label">Email</span>
            <span class="direct-connection-details__value">{{ grant.email }}</span>
            <!-- DELETE -->
            <dd-icon-button
              :is-disabled="isUpdating"
              danger
              title="Delete connection"
              icon="trash"
              type="rounded"
              size="medium"
              color="grey"
              class="direct-connection-details__delete"
              @click="confirmDeleteGrant()"
            />
          </div>
          <div class="direct-connection-details__row">
            <span class="direct-connection-details__label">Provider</span>
            <span class="direct-connection-details__value">{{ grant.provider }}</span>
          </div>
          <div class="direct-connection-details__row">
            <span class="direct-connection-details__label">Status</span>
            <span
              :class="grant.isValid
                ? 'direct-connection-details__status--valid'
                : 'direct-connection-details__status--invalid'"
              class="direct-connection-details__status"
            >
              <i :class="grant.isValid ? 'ddi-check' : 'ddi-warning'" class="ddi" />
              {{ grant.isValid ? 'Connected' : 'Connection Error' }}
            </span>
          </div>
          <div v-if="isShared" class="direct-connection-details__row">
            <span class="direct-connection-details__label">Shared with</span>
            <span class="direct-connection-details__value">{{ otherMailboxNames }}</span>
          </div>
        </div>

        <div class="dd-divider" />

        <el-form :disabled="isUpdating">
          <el-form-item prop="name">
            <h5>Sender Name</h5>
            <el-input v-model="grantName" maxlength="50" />
            <p v-if="isShared" class="direct-connection-hint">
              Only emails from this mailbox use this name.
            </p>
          </el-form-item>
        </el-form>

        <dd-button
          :is-disabled="isUpdating"
          :is-loading="isUpdating"
          :color="ColorAssistant.MailboxColor"
          size="large"
          class="float-right"
          @click="updateGrant()"
        >Save Changes</dd-button>
        <div class="clearfix" />

        <div class="dd-divider" />

        <div class="direct-connection-actions">
          <!-- RE-CONNECT (fixes the connection for every mailbox sharing it) -->
          <dd-button
            :color="ColorAssistant.MailboxColor"
            size="medium"
            @click="connect()"
          >
            <span>Re-connect</span>
          </dd-button>
          <!-- REMOVE (only this mailbox, when others keep using the connection) -->
          <dd-button
            v-if="isShared"
            color="strawberry"
            size="medium"
            @click="confirmRemoveMailbox()"
          >
            <span>Remove Connection</span>
          </dd-button>
        </div>
      </template>

      <!-- NO GRANT -->
      <template v-else>
        <!-- SHARE AN EXISTING CONNECTION (doesn't count against the limit) -->
        <template v-if="connections.length > 0">
          <h5>Use an existing connection</h5>
          <p class="direct-connection-empty">
            Share an email account this account has already connected. Mailboxes on the same connection send from its address,
            and each email it receives goes to one of them based on their filters and priority.
          </p>
          <div class="direct-connection-share">
            <single-select
              v-model="selectedConnectionID"
              :disabled="isUpdating"
              placeholder="Choose a connection"
              class="direct-connection-share__select"
            >
              <el-option
                v-for="connection in connections"
                :key="connection.id"
                :value="connection.id"
                :label="connectionLabel(connection)"
              />
            </single-select>
            <dd-button
              :is-disabled="isUpdating || !selectedConnectionID"
              :is-loading="isUpdating"
              :color="ColorAssistant.MailboxColor"
              size="medium"
              @click="linkConnection()"
            >
              <span>Use Connection</span>
            </dd-button>
          </div>

          <div class="dd-divider" />

          <h5>Connect a new email account</h5>
        </template>

        <guest-upgrade-prompt
          v-if="$permissions.isAtDirectMailboxLimit && $permissions.canUpgradeToProfessionalPlan"
          title="Upgrade to Pro"
          body="Get additional direct mailbox connections by upgrading to the Pro Plan."
          lead="Upgrading unlocks more direct mailbox connections and the rest of the Professional feature set."
          no-self-upgrade-copy="Direct mailbox connections let you sync and send emails with minimal configuration. Ask your account owner to upgrade to Pro."
          mode="hero"
        />

        <p v-else-if="$permissions.isAtDirectMailboxLimit" class="direct-connection-empty">
          You have reached the limit of direct mailbox connections for your plan. Contact <a href="mailto:support@donedone.com">support@donedone.com</a> for more information about upgrading.
        </p>

        <template v-else>
          <p class="direct-connection-empty">
            Connect directly to your email provider (Google, Microsoft, IMAP, etc.) by logging in and granting access.
          </p>
          <dd-button
            :color="ColorAssistant.MailboxColor"
            size="medium"
            @click="connect()"
          >
            <span>Connect</span>
          </dd-button>
        </template>
      </template>
    </mailbox-settings-collapsible-card>

    <!-- MAILBOX PRIORITY (only when other mailboxes share the connection) -->
    <mailbox-settings-direct-connection-routing
      v-if="hasGrant && isShared"
      :id="id"
      :email="grant.email"
      :mailboxes="connectionMailboxes"
      @sorted="(mailboxes) => connectionMailboxes = mailboxes"
    />

    <!-- EMAIL FILTERS (only for a valid direct connection) -->
    <mailbox-settings-direct-connection-filters
      v-if="hasGrant && grant.isValid"
      :id="id"
      :is-shared="isShared"
      @filters-changed="getConnectionMailboxes()"
    />
  </mailbox-settings-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import config from '@/config'
import MailboxSettingsLayout from '@/layouts/mailbox-settings-layout.vue'

/* Mixins */

/* Components */
import EntitySettingsLoading from '@/layouts/entity-settings-loading.vue'
import MailboxSettingsCollapsibleCard from '@/screens/mailbox-settings/components/mailbox-settings-collapsible-card.vue'
import MailboxSettingsDirectConnectionFilters from '@/screens/mailbox-settings/components/mailbox-settings-direct-connection-filters.vue'
import MailboxSettingsDirectConnectionRouting from '@/screens/mailbox-settings/components/mailbox-settings-direct-connection-routing.vue'

/* Helpers */
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import {
  IConnection,
  IConnectionMailbox,
  IGrant,
} from '@/api/types/response.types'

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'

export default defineComponent({
  name: 'MailboxSettingsDirectConnection',

  components: {
    MailboxSettingsLayout,
    EntitySettingsLoading,
    MailboxSettingsCollapsibleCard,
    MailboxSettingsDirectConnectionFilters,
    MailboxSettingsDirectConnectionRouting,
  },

  data() {
    return {
      ColorAssistant,
      isLoading: true,
      isUpdating: false,
      name: '',
      grantName: '',
      grant: null as IGrant | null,
      /* The mailboxes on this mailbox's connection, highest priority first */
      connectionMailboxes: [] as IConnectionMailbox[],
      /* The account's connections, for a mailbox that isn't connected yet */
      connections: [] as IConnection[],
      selectedConnectionID: null as number | null,
    }
  },

  head(): any {
    return {
      title: `Direct Connection - ${this.name}`,
    }
  },

  computed: {
    accountID(): number {
      return parseInt(this.$route.params.accountId, 10)
    },
    id(): number {
      return parseInt(this.$route.params.id, 10)
    },
    hasGrant(): boolean {
      return !!this.grant
    },
    isShared(): boolean {
      return this.connectionMailboxes.length > 1
    },
    otherMailboxNames(): string {
      return this.connectionMailboxes
        .filter(m => m.mailboxID !== this.id)
        .map(m => m.name)
        .join(', ')
    },
    connectedError(): boolean {
      return new URLSearchParams(window.location.search).has('error')
    },
    connectedSuccessfully(): boolean {
      return new URLSearchParams(window.location.search).has('connected')
    },
  },

  async created() {
    this.isLoading = true
    await Promise.all([this.getMailboxName(), this.getGrant()])
    await (this.hasGrant
      ? this.getConnectionMailboxes()
      : this.getConnections())
    this.isLoading = false

    if (this.connectedSuccessfully) {
      this.$messaging.showSuccess(
        'Connection successful! Your mailbox is now directly connected to your email provider.',
      )
    } else if (this.connectedError) {
      this.$messaging.showError(this.$route.query.error as string)
    }
  },

  methods: {
    async getMailboxName() {
      try {
        const response = await MailboxesAPI.get_GeneralSettings(this.id)
        this.name = response.name
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async getGrant() {
      try {
        const response = await MailboxesAPI.get_Grant(this.id)
        // The API returns an empty/falsy body when there is no grant.
        this.grant = response && response.grantID ? response : null
        this.grantName = this.grant ? this.grant.name : ''
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async getConnectionMailboxes() {
      try {
        this.connectionMailboxes = await MailboxesAPI.get_GrantMailboxes(
          this.id,
        )
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async getConnections() {
      try {
        this.connections = await MailboxesAPI.get_GrantConnections(this.id)
      } catch (error) {
        this.$error.emit(error)
      }
    },
    connectionLabel(connection: IConnection): string {
      const usage = connection.mailboxes.length
        ? `used by ${connection.mailboxes.map(m => m.name).join(', ')}`
        : 'not used by any mailbox'

      return `${connection.email} (${usage})${
        connection.isValid ? '' : ' - Connection Error'
      }`
    },
    async updateGrant() {
      try {
        this.isUpdating = true
        const response = await MailboxesAPI.put_Grant(this.id, this.grantName)
        if (this.grant) {
          this.grant.name = this.grantName
        }
        this.$messaging.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
        this.grantName = this.grant ? this.grant.name : ''
      } finally {
        this.isUpdating = false
      }
    },
    async linkConnection() {
      try {
        this.isUpdating = true
        await MailboxesAPI.post_GrantLink(this.id, this.selectedConnectionID)
        // Reload so the settings menu switches to the direct connection's options
        window.location.search = 'connected'
      } catch (error) {
        this.$error.emit(error, true)
        this.isUpdating = false
      }
    },
    connect() {
      const state = `${this.accountID}|${this.id}`

      const redirectURI = `${config.serverSideAppUrl}/security/nylas/oauth2/sign-in`

      let url = `https://api.us.nylas.com/v3/connect/auth?access_type=offline&response_type=code&client_id=${
        config.nylasClientId
      }&state=${encodeURIComponent(state)}&redirect_uri=${encodeURIComponent(
        redirectURI,
      )}`

      if (this.grant && this.grant.email != null) {
        url = url.concat(`&login_hint=${encodeURIComponent(this.grant.email)}`)
      }

      window.location.href = url
    },
    confirmRemoveMailbox() {
      this.$confirm(
        `Removing this connection will stop DoneDone from sending and receiving
        email through ${this.grant.email} for it. ${
          this.otherMailboxNames
        } will keep using the connection. Are you sure?`,
        'Warning!',
        {
          confirmButtonText: 'Yes, remove this connection',
          cancelButtonText: 'Cancel',
          confirmButtonClass: 'el-button--strawberry',
          type: 'warning',
          center: true,
        },
      ).then(async () => {
        await this.removeMailbox()
      })
    },
    async removeMailbox() {
      try {
        this.isUpdating = true
        await MailboxesAPI.delete_GrantDelete(this.id)
        window.location.reload()
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isUpdating = false
      }
    },
    confirmDeleteGrant() {
      const affected = this.isShared
        ? `every mailbox that shares it (${this.connectionMailboxes
            .map(m => m.name)
            .join(', ')})`
        : 'this mailbox'

      this.$confirm(
        `Deleting this connection will stop DoneDone from sending and receiving
        email through your provider for ${affected}. Are you sure?`,
        'Warning!',
        {
          confirmButtonText: 'Yes, delete this connection',
          cancelButtonText: 'Cancel',
          confirmButtonClass: 'el-button--strawberry',
          type: 'warning',
          center: true,
        },
      ).then(async () => {
        await this.deleteGrant()
      })
    },
    async deleteGrant() {
      try {
        this.isUpdating = true
        await MailboxesAPI.delete_GrantConnectionDelete(this.id)
        window.location.reload()
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isUpdating = false
      }
    },
  },
})
</script>

<style lang="scss">
.mailbox-settings-direct-connection {
  margin-bottom: 20px;

  .direct-connection-details {
    &__row {
      display: flex;
      align-items: center;
      padding: 8px 0;
    }

    &__label {
      flex: 0 0 100px;
      color: var(--color-text-third);
    }

    &__value {
      color: var(--color-text-secondary);
    }

    &__delete {
      margin-left: auto;
    }

    &__status {
      display: inline-flex;
      align-items: center;

      > .ddi {
        margin-right: 6px;
      }

      &--valid {
        color: $color--green;
      }

      &--invalid {
        color: $strawberry;
      }
    }
  }

  .direct-connection-actions {
    display: flex;
    gap: 12px;
  }

  .direct-connection-empty {
    color: var(--color-text-third);
    margin-bottom: 16px;
  }

  .direct-connection-hint {
    color: var(--color-text-third);
    font-size: 12px;
    margin: 6px 0 0;
  }

  .direct-connection-share {
    display: flex;
    align-items: center;
    gap: 12px;

    &__select {
      flex: 1;
      min-width: 0;
    }
  }
}
</style>
